import { useEffect, useMemo, useRef, useState } from "react";
import {
  Headphones,
  Loader2,
  Pause,
  Play,
  Rewind,
  FastForward,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { buildBeats, motifFor } from "@/lib/cinematic/beats";
import { runParticleField } from "@/lib/cinematic/particles";
import { getArticleAudio } from "@/lib/article-audio.functions";
import { track } from "@/lib/analytics";
import { trackConversion, trackConversionOnce } from "@/hooks/use-page-view";
import { isUnsafeImageReference, sizedImage } from "@/lib/asset-map";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";
import { playbackVisuals, visualClock } from "@/lib/visual-assets";
import { shouldRestoreStoryAudio } from "@/lib/story-audio-progress";

/** Gallery frames can be real motion clips (Wikimedia Commons webm/ogv). */
const isClipUrl = (u?: string | null) => !!u && /\.(webm|ogv|ogg|mp4)(\?|$)/i.test(u);

// Smartasaurus Story Mode - single playback engine.
//
// ONE narration. ONE progress state. ONE Media Session. ONE Web Audio graph.
// The hero owns the <audio> element, the broadcast-clean mastering chain,
// and the silence monitor. There is no separate "Listen to this article"
// player anywhere in the app - this component is the only entry point.
// BackgroundMusic listens for `smartasaurus:narration*` events and ducks
// smoothly under narration.

type Props = {
  slug: string;
  title: string;
  body?: string | null;
  audioScript?: string | null;
  /** Localized captions. Audio timing always comes from audioScript. */
  captionScript?: string | null;
  excerpt?: string | null;
  heroImageUrl?: string | null;
  midImageUrl?: string | null;
  imageGallery?: Array<{
    url: string;
    concept?: string;
    beat?: string;
    sourceTitle?: string;
    originalUrl?: string;
    assetKey?: string;
    sha256?: string;
    perceptualHash?: string;
    startRatio?: number;
    endRatio?: number;
    reviewStatus?: string;
  }> | null;
  categoryLabel?: string | null;
  categorySlug?: string | null;
  initialAudioUrl?: string | null;
  /** Translated narration is in charge: keep the visuals, silence the English voice. */
  silentNarration?: boolean;
  /** Mirror the translated player's play/pause state onto the story. */
  externalPlaying?: boolean;
  /** Translated narration clock; avoids loading or playing the English track. */
  externalCurrent?: number;
  externalDuration?: number;
};

const AMBIENT_SECONDS_PER_BEAT = 4.5;

const CAMERA_POOL = [
  "push-in",
  "pull-back",
  "pan-left",
  "pan-right",
  "tilt-up",
  "orbit",
  "rack-focus",
  "hold",
] as const;

function localAudioSrc(slug: string) {
  return `/api/public/audio/${encodeURIComponent(slug)}`;
}

const STORY_PROGRESS_PREFIX = "sa.story-progress.";

function readStoryProgress(slug: string): number {
  try {
    const value = Number(window.localStorage.getItem(`${STORY_PROGRESS_PREFIX}${slug}`));
    return Number.isFinite(value) && value >= 3 ? value : 0;
  } catch {
    return 0;
  }
}

function writeStoryProgress(slug: string, seconds: number, duration?: number) {
  try {
    if (!Number.isFinite(seconds) || seconds < 3) return;
    if (duration && Number.isFinite(duration) && duration - seconds < 4) {
      window.localStorage.removeItem(`${STORY_PROGRESS_PREFIX}${slug}`);
      return;
    }
    window.localStorage.setItem(`${STORY_PROGRESS_PREFIX}${slug}`, String(Math.floor(seconds)));
  } catch {
    /* Storage can be unavailable in private browsing. Playback still works. */
  }
}

function clearStoryProgress(slug: string) {
  try {
    window.localStorage.removeItem(`${STORY_PROGRESS_PREFIX}${slug}`);
  } catch {
    /* ignore */
  }
}

function seededShuffle<T>(arr: readonly T[], seedStr: string): T[] {
  let h = 2166136261;
  for (let i = 0; i < seedStr.length; i++) {
    h ^= seedStr.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    const j = Math.abs(h) % (i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function cameraTransform(camera: string, t: number): string {
  const ease = (x: number) => 1 - Math.pow(1 - x, 3);
  const e = ease(t);
  switch (camera) {
    case "push-in":
      return `scale(${1.06 + e * 0.09}) translate3d(${(-2 + e * -1).toFixed(2)}%, ${(1 + e * -1).toFixed(2)}%, 0)`;
    case "pull-back":
      return `scale(${1.18 - e * 0.1}) translate3d(${(e * 2).toFixed(2)}%, ${(e * -1).toFixed(2)}%, 0)`;
    case "pan-left":
      return `scale(1.14) translate3d(${(3 - e * 6).toFixed(2)}%, ${(-1 + e * 0.5).toFixed(2)}%, 0)`;
    case "pan-right":
      return `scale(1.14) translate3d(${(-3 + e * 6).toFixed(2)}%, ${(1 - e * 0.5).toFixed(2)}%, 0)`;
    case "tilt-up":
      return `scale(1.16) translate3d(0%, ${(3 - e * 6).toFixed(2)}%, 0)`;
    case "orbit":
      return `scale(${1.1 + Math.sin(e * Math.PI) * 0.04}) translate3d(${(Math.sin(e * Math.PI * 2) * 2).toFixed(2)}%, ${(Math.cos(e * Math.PI * 2) * 1.4).toFixed(2)}%, 0)`;
    case "rack-focus":
      return `scale(${1.08 + e * 0.04}) translate3d(0%, 0%, 0)`;
    case "hold":
    default:
      return `scale(${1.05 + e * 0.02}) translate3d(0%, 0%, 0)`;
  }
}

function timedBeatIndex(
  beats: Array<{ startRatio?: number; endRatio?: number }>,
  elapsed: number,
  total: number,
): number {
  if (!beats.length) return 0;
  const ratio = total > 0 ? Math.max(0, Math.min(0.999999, elapsed / total)) : 0;
  const idx = beats.findIndex((b, i) => {
    const start = b.startRatio ?? i / beats.length;
    const end = b.endRatio ?? (i + 1) / beats.length;
    return ratio >= start && (ratio < end || i === beats.length - 1);
  });
  return idx >= 0 ? idx : Math.min(beats.length - 1, Math.floor(ratio * beats.length));
}

function timedBeatProgress(
  beat: { startRatio?: number; endRatio?: number } | undefined,
  elapsed: number,
  total: number,
): number {
  if (!beat || total <= 0) return 0;
  const ratio = Math.max(0, Math.min(1, elapsed / total));
  const start = beat.startRatio ?? 0;
  const end = beat.endRatio ?? 1;
  const span = Math.max(0.001, end - start);
  return Math.max(0, Math.min(1, (ratio - start) / span));
}

function rackFocusBlur(camera: string, t: number): string {
  if (camera !== "rack-focus") return "blur(0px)";
  const b = t < 0.5 ? t * 2 * 6 : (1 - t) * 2 * 6;
  return `blur(${b.toFixed(2)}px)`;
}

// Caption renderer.
// Rules learned the hard way:
//  1. Escape ONCE, and never let the emphasis regex look at HTML entities -
//     matching "39" inside "&#39;" wraps the digits in <mark>, and if that
//     <mark> animates from opacity 0 the apostrophe renders as "isn&#  ;t".
//  2. No pop/flash animation on emphasis - just a static gradient underline.
//  3. Emojis are sprinkled, not stamped on every line, and rotated by topic.
const CAPTION_EMPHASIS_WORDS_SRC =
  "\\b(never|always|impossible|actually|secret|hidden|deadly|lethal|venom|poison|" +
  "giant|tiny|ancient|survive|survives|explode|freeze|glow|glowing|billion|million|" +
  "thousand|percent|forever|instantly|only|first|last|entire|whole|nobody|" +
  "everyone|everything|literally|seriously|because|why|how)\\b";
const CAPTION_NUMBER_SRC =
  "\\b\\d[\\d.,]*(?:\\s?(?:%|x|×|°C|°F|kg|g|m|km|mm|cm|ft|lbs|years?))?\\b";
const CAPTION_EMPH_RE = new RegExp(`(${CAPTION_NUMBER_SRC})|(${CAPTION_EMPHASIS_WORDS_SRC})`, "gi");
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
function renderCaptionHtml(text: string, emoji: string | null): string {
  if (!text) return "";
  // Walk the ORIGINAL (un-escaped) text; escape non-match gaps and wrap
  // matches. This keeps entities out of the emphasis regex path.
  const re = new RegExp(CAPTION_EMPH_RE.source, "gi");
  let out = "";
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out += escapeHtml(text.slice(last, m.index));
    out += `<mark class="cap-emph">${escapeHtml(m[0])}</mark>`;
    last = m.index + m[0].length;
  }
  if (last < text.length) out += escapeHtml(text.slice(last));
  return emoji ? `${out} <span class="cap-emoji" aria-hidden>${emoji}</span>` : out;
}
const EMOJI_POOLS: Record<string, string[]> = {
  bubbles: ["🌊", "💧", "🐠", "🫧", "🐙"],
  stars: ["✨", "🌌", "🪐", "☄️", "🔭"],
  cells: ["🧬", "🧠", "🫀", "🔬", "🧫"],
  embers: ["🔥", "⚡", "🐾", "🦴", "🌋"],
  spores: ["🍄", "🌱", "🌿", "🪴", "🍃"],
  snow: ["❄️", "🧊", "🐧", "🏔️", "🌨️"],
  dust: ["✨", "🌍", "🧭", "💡", "🔎"],
};
function pickCaptionEmoji(motif: string, beatIndex: number, totalBeats: number): string | null {
  if (totalBeats <= 0) return null;
  if (beatIndex === 0 || beatIndex === totalBeats - 1) return null;
  if (beatIndex % 3 !== 1) return null; // ~1 in 3, evenly spaced
  const pool = EMOJI_POOLS[motif] ?? EMOJI_POOLS.dust;
  return pool[Math.floor(beatIndex / 3) % pool.length];
}

function isIOSLikeDevice() {
  try {
    const ua = navigator.userAgent || "";
    const platform = (navigator as any).platform || "";
    return (
      /iPad|iPhone|iPod/.test(ua) ||
      (platform === "MacIntel" && (navigator as any).maxTouchPoints > 1)
    );
  } catch {
    return false;
  }
}

export function CinematicHero({
  slug,
  title,
  body,
  audioScript,
  captionScript,
  excerpt,
  heroImageUrl,
  midImageUrl,
  imageGallery,
  categoryLabel,
  categorySlug,
  initialAudioUrl,
  silentNarration = false,
  externalPlaying,
  externalCurrent,
  externalDuration,
}: Props) {
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const localizedCaptionText = captionScript?.trim() || "";
  const englishCaptionText = audioScript?.trim() || body || "";
  const hasLocalizedCaptionTrack = lang !== "en" && localizedCaptionText.length >= 80;
  const [captionLanguage, setCaptionLanguage] = useState<"en" | "localized">(
    hasLocalizedCaptionTrack ? "localized" : "en",
  );
  // Remember an explicit viewer choice; only a new article or language resets it.
  const captionChoiceRef = useRef<{ key: string; picked: boolean }>({ key: `${lang}|${slug}`, picked: false });
  useEffect(() => {
    const key = `${lang}|${slug}`;
    if (captionChoiceRef.current.key !== key) captionChoiceRef.current = { key, picked: false };
    if (captionChoiceRef.current.picked) return;
    setCaptionLanguage(hasLocalizedCaptionTrack ? "localized" : "en");
  }, [lang, slug, hasLocalizedCaptionTrack]);
  const beatText = captionLanguage === "localized" && hasLocalizedCaptionTrack
    ? localizedCaptionText
    : englishCaptionText;
  const timingText = audioScript?.trim() || body || "";
  const beats = useMemo(
    () => buildBeats({ title, body: beatText, timingBody: timingText, excerpt }),
    [title, beatText, timingText, excerpt],
  );
  const motif = useMemo(() => motifFor(categorySlug, categoryLabel), [categorySlug, categoryLabel]);
  const cameraSeq = useMemo(() => seededShuffle(CAMERA_POOL, slug), [slug]);

  // ---- Audio engine (single source of truth) ----
  const fetchAudio = useServerFn(getArticleAudio);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const graphBuiltRef = useRef(false);
  const silenceStateRef = useRef<{ silent: boolean; since: number }>({ silent: true, since: 0 });
  const silenceRafRef = useRef<number | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const firstAudioLoggedRef = useRef(false);
  const autoPlayRef = useRef(false);
  const pendingResumeRef = useRef(0);
  const lastSavedSecondRef = useRef(0);
  const recoveryAttemptedRef = useRef(false);
  const restoredSlugRef = useRef<string | null>(null);

  const [src, setSrc] = useState<string | null>(initialAudioUrl ? localAudioSrc(slug) : null);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [visualBeatIndex, setVisualBeatIndex] = useState(0);
  const [visualFrameIndex, setVisualFrameIndex] = useState(0);
  const [showMissionOutro, setShowMissionOutro] = useState(false);
  const visualBeatRef = useRef(0);
  const visualFrameRef = useRef(0);
  const missionOutroTimerRef = useRef<number | null>(null);
  const hasTimedTranscript = (timingText.length ?? 0) >= 80;

  useEffect(() => {
    const saved = readStoryProgress(slug);
    pendingResumeRef.current = saved;
    lastSavedSecondRef.current = Math.floor(saved);
    setSrc(initialAudioUrl ? localAudioSrc(slug) : null);
    setPlaying(false);
    setCurrent(saved);
    setVisualBeatIndex(0);
    setVisualFrameIndex(0);
    setShowMissionOutro(false);
    visualBeatRef.current = 0;
    visualFrameRef.current = 0;
    setError(null);
    autoPlayRef.current = false;
    recoveryAttemptedRef.current = false;
    restoredSlugRef.current = null;
    firstAudioLoggedRef.current = false;
    if (missionOutroTimerRef.current) {
      window.clearTimeout(missionOutroTimerRef.current);
      missionOutroTimerRef.current = null;
    }
  }, [slug, initialAudioUrl]);

  // Metadata can load before React attaches its event handlers from hydrated
  // markup. Apply saved progress from an effect as well as the media events so
  // refresh and back/forward restoration are deterministic on fast connections.
  useEffect(() => {
    if (!src || restoredSlugRef.current === slug) return;
    const el = audioRef.current;
    if (!el) return;
    const restore = () => {
      const savedProgress = Math.max(pendingResumeRef.current, readStoryProgress(slug));
      if (!Number.isFinite(el.duration) || el.duration <= 0) return;
      if (savedProgress > 0) {
        el.currentTime = Math.min(savedProgress, Math.max(0, el.duration - 1));
        setCurrent(el.currentTime);
      }
      pendingResumeRef.current = 0;
      restoredSlugRef.current = slug;
    };
    if (el.readyState >= 1) restore();
    else el.addEventListener("loadedmetadata", restore, { once: true });
    return () => el.removeEventListener("loadedmetadata", restore);
  }, [slug, src]);

  // Pause on tab hide / unload / route change. Do not destroy the source when
  // merely hidden: mobile browsers keep the component mounted and returning
  // to an emptied element created the dead "Resume Story" state.
  useEffect(() => {
    const saveAndPause = () => {
      const el = audioRef.current;
      if (el) {
        writeStoryProgress(slug, el.currentTime, el.duration);
        el.pause();
      }
    };
    const onVis = () => {
      if (document.hidden) saveAndPause();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", saveAndPause);
    window.addEventListener("beforeunload", saveAndPause);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", saveAndPause);
      window.removeEventListener("beforeunload", saveAndPause);
      saveAndPause();
      const el = audioRef.current;
      if (el) {
        el.removeAttribute("src");
        el.load();
      }
      try {
        audioCtxRef.current?.close();
      } catch {
        /* ignore */
      }
    };
  }, [slug]);

  function buildAudioGraph() {
    // Keep narration as a plain HTMLAudioElement. Routing media through WebAudio
    // can mute cross-origin signed files on Safari/Chrome and was the source of
    // “audio disappeared” reports. No EQ/compressor/analyser here: no distortion,
    // no CORS-induced silence, no paid regeneration needed.
    graphBuiltRef.current = true;
  }

  function startSilenceMonitor() {
    const analyser = analyserRef.current;
    if (!analyser) return;
    if (silenceRafRef.current) cancelAnimationFrame(silenceRafRef.current);
    const buf = new Uint8Array(analyser.fftSize);
    const SPEECH_RMS = 0.045;
    const SILENCE_RMS = 0.02;
    const SILENCE_HOLD_MS = 650;
    const SPEECH_HOLD_MS = 90;
    const emit = (silent: boolean) => {
      if (silenceStateRef.current.silent === silent) return;
      silenceStateRef.current.silent = silent;
      window.dispatchEvent(
        new CustomEvent("smartasaurus:narration-silence", { detail: { silent } }),
      );
    };
    const tick = () => {
      const el = audioRef.current;
      if (!el || el.paused || el.ended) {
        emit(true);
        silenceRafRef.current = null;
        return;
      }
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);
      const now = performance.now();
      const state = silenceStateRef.current;
      if (rms > SPEECH_RMS) {
        if (state.silent) {
          if (state.since === 0) state.since = now;
          if (now - state.since > SPEECH_HOLD_MS) {
            emit(false);
            state.since = 0;
          }
        } else {
          state.since = 0;
        }
      } else if (rms < SILENCE_RMS) {
        if (!state.silent) {
          if (state.since === 0) state.since = now;
          if (now - state.since > SILENCE_HOLD_MS) {
            emit(true);
            state.since = 0;
          }
        } else {
          state.since = 0;
        }
      } else {
        state.since = 0;
      }
      silenceRafRef.current = requestAnimationFrame(tick);
    };
    silenceRafRef.current = requestAnimationFrame(tick);
  }
  function stopSilenceMonitor() {
    if (silenceRafRef.current) cancelAnimationFrame(silenceRafRef.current);
    silenceRafRef.current = null;
  }

  async function ensureAudio(): Promise<string | null> {
    if (src) return src;
    setPreparing(true);
    setError(null);
    try {
      const res = await fetchAudio({ data: { slug } });
      if (res.status === "ok" && res.audio_url) {
        const nextSrc = localAudioSrc(slug);
        setSrc(nextSrc);
        return nextSrc;
      }
      setError(
        ui("Narration is not available for this story yet. The full article is below."),
      );
      track("narration_error", { slug, status: res.status });
      return null;
    } catch {
      setError(ui("Story unavailable."));
      track("narration_error", { slug, status: "exception" });
      return null;
    } finally {
      setPreparing(false);
    }
  }

  async function handleToggle() {
    setError(null);
    setShowMissionOutro(false);
    if (missionOutroTimerRef.current) {
      window.clearTimeout(missionOutroTimerRef.current);
      missionOutroTimerRef.current = null;
    }
    const el = audioRef.current;
    if (playing && el) {
      el.pause();
      return;
    }
    buildAudioGraph();
    if (audioCtxRef.current?.state === "suspended") {
      await audioCtxRef.current.resume().catch(() => {});
    }
    if (src && el) {
      if (!el.currentSrc || el.error) {
        recoveryAttemptedRef.current = true;
        autoPlayRef.current = true;
        pendingResumeRef.current = Math.max(pendingResumeRef.current, current);
        el.src = src;
        el.load();
        return;
      }
      el.play().catch(() => {
        pendingResumeRef.current = Math.max(pendingResumeRef.current, el.currentTime, current);
        autoPlayRef.current = true;
        recoveryAttemptedRef.current = true;
        el.src = src;
        el.load();
      });
      return;
    }
    startedAtRef.current = performance.now();
    track("narration_play_pressed", { slug });
    autoPlayRef.current = true;
    await ensureAudio();
  }

  function skip(delta: number) {
    const el = audioRef.current;
    if (!el || !Number.isFinite(el.duration)) return;
    el.currentTime = Math.max(0, Math.min(el.duration, el.currentTime + delta));
  }

  // ---- Translated-narration mirroring ----
  // Translated narration owns the visual clock. The English track stays paused
  // and is never fetched merely to animate the story.
  useEffect(() => {
    const el = audioRef.current;
    if (el) el.muted = silentNarration;
  }, [silentNarration, src]);

  useEffect(() => {
    if (externalPlaying === undefined) return;
    const el = audioRef.current;
    if (el && !el.paused) el.pause();
  }, [externalPlaying]);

  // Allow legacy events (BackgroundMusic + other components) to trigger toggle.
  useEffect(() => {
    const onReq = (e: Event) => {
      const detail = (e as CustomEvent<{ slug?: string }>).detail;
      if (detail?.slug && detail.slug !== slug) return;
      void handleToggle();
    };
    window.addEventListener("smartasaurus:narration-request-toggle", onReq as EventListener);
    return () =>
      window.removeEventListener("smartasaurus:narration-request-toggle", onReq as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, src, playing]);

  // ---- Ambient timeline before narration takes over ----
  const [ambient, setAmbient] = useState(0);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const externalActive =
    externalPlaying !== undefined && (externalPlaying || (externalCurrent ?? 0) > 0);
  const narrationStarted = playing || current > 0 || externalActive;

  useEffect(() => {
    if (narrationStarted) return;
    const step = (ts: number) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;
      setAmbient((v) => v + dt);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastTsRef.current = null;
    };
  }, [narrationStarted]);

  const usingExternal = externalActive && (externalDuration ?? 0) > 0;
  const usingNarration = usingExternal || (narrationStarted && duration > 0);
  const totalSeconds = usingExternal
    ? (externalDuration ?? 0)
    : usingNarration
      ? duration
      : beats.length * AMBIENT_SECONDS_PER_BEAT;
  const elapsedSeconds = usingExternal
    ? (externalCurrent ?? 0)
    : usingNarration
      ? current
      : ambient % Math.max(1, totalSeconds);
  const beatIndex = Math.min(beats.length - 1, Math.max(0, visualBeatIndex));
  const beat = beats[beatIndex];
  const beatProgress = timedBeatProgress(beat, elapsedSeconds, totalSeconds);
  const progressPct = (elapsedSeconds / totalSeconds) * 100;

  // Particle field.
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (!canvasRef.current) return;
    const stop = runParticleField(canvasRef.current, motif);
    return stop;
  }, [motif]);

  // Visual sequence. Some frames are real Commons clips (webm/ogv) spliced
  // between the stills. Any browser that cannot decode one drops that clip
  // from the sequence and keeps playing the stills.
  const [failedClips, setFailedClips] = useState<string[]>([]);
  const markClipFailed = (url: string) =>
    setFailedClips((prev) => (prev.includes(url) ? prev : [...prev, url]));
  // Deduplicate: legacy rows padded the same URL up to 20 times, which made the
  // reel look like a single frozen image.
  const playbackFrames = playbackVisuals(
      (imageGallery ?? [])
        .filter(
          (g) =>
            !isUnsafeImageReference(
              g.url,
              `${g.sourceTitle ?? ""} ${g.concept ?? ""} ${g.beat ?? ""}`,
            ),
        )
        .filter((g) => !failedClips.includes(g.url)),
  );
  const gallery = playbackFrames.map((g) => g.url);
  const safeHeroImageUrl =
    heroImageUrl && !isUnsafeImageReference(heroImageUrl, title) ? heroImageUrl : null;
  const safeMidImageUrl =
    midImageUrl && !isUnsafeImageReference(midImageUrl, title) ? midImageUrl : null;
  // A legacy one-frame gallery must not masquerade as a slideshow. Until the
  // worker has accumulated multiple verified frames, use the cover/mid pair.
  const hasGallery = gallery.length >= 8;
  const getImageUrl = (idx: number) => {
    if (!hasGallery) {
      const primary = safeHeroImageUrl || safeMidImageUrl || null;
      return primary;
    }
    const imgIdx = Math.max(0, Math.min(idx, gallery.length - 1));
    return gallery[imgIdx];
  };

  // Parity-based two-slot crossfade. Slot A always holds the image for
  // even-parity beat indices, slot B for odd. When the beat boundary flips,
  // the slot that was fading IN is already at full opacity and simply stays;
  // the other slot silently swaps its src (invisible at opacity 0). This
  // eliminates the flash caused by remounting <img key={url}> at boundaries.
  const parity = ((visualFrameIndex % 2) + 2) % 2;
  const slotAIdx = parity === 0 ? visualFrameIndex : visualFrameIndex + 1;
  const slotBIdx = parity === 0 ? visualFrameIndex + 1 : visualFrameIndex;
  // Full-resolution Commons uploads can be several MB each; the slideshow
  // paints them at screen width, so request a screen-sized copy instead.
  const slotAUrl = sizedImage(getImageUrl(slotAIdx), 1280) || null;
  const slotBUrl = sizedImage(getImageUrl(slotBIdx), 1280) || null;
  const currentUrl = parity === 0 ? slotAUrl : slotBUrl;

  const grade = beat?.grade;
  const filter = grade
    ? `saturate(${grade.saturate}) contrast(${grade.contrast}) hue-rotate(${grade.hue}deg) brightness(${grade.brightness})`
    : "none";

  // Preload all gallery URLs once so the offscreen slot's src swap decodes
  // from cache the instant it happens (no blank-frame flash).
  useEffect(() => {
    if (!hasGallery) return;
    const nodes: HTMLImageElement[] = [];
    for (const url of gallery) {
      if (isClipUrl(url)) continue; // clips stream themselves
      const img = new Image();
      img.decoding = "async";
      img.src = url;
      nodes.push(img);
    }
    return () => {
      nodes.length = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gallery.join("|")]);

  // --- Smooth 60fps camera engine ---
  // Drive Ken Burns transform + dissolve directly on DOM refs from a rAF loop
  // reading the live audio clock. Bypassing React state avoids the chunky
  // "frame-freeze" caused by 4-15 Hz `timeupdate` events snapping transforms.
  const imgARef = useRef<HTMLElement | null>(null);
  const imgBRef = useRef<HTMLElement | null>(null);
  const captionRef = useRef<HTMLParagraphElement | null>(null);
  const progressFillRef = useRef<HTMLDivElement | null>(null);
  const progressThumbRef = useRef<HTMLDivElement | null>(null);
  const cameraRafRef = useRef<number | null>(null);

  // Refs mirror volatile values so the rAF loop never has to tear down.
  const filterRef = useRef(filter);
  const totalSecondsRef = useRef(totalSeconds);
  const elapsedSecondsRef = useRef(elapsedSeconds);
  const usingNarrationRef = useRef(usingNarration);
  const usingExternalRef = useRef(usingExternal);
  const externalCurrentRef = useRef(externalCurrent ?? 0);
  const externalDurationRef = useRef(externalDuration ?? 0);
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);
  useEffect(() => {
    totalSecondsRef.current = totalSeconds;
  }, [totalSeconds]);
  useEffect(() => {
    elapsedSecondsRef.current = elapsedSeconds;
  }, [elapsedSeconds]);
  useEffect(() => {
    usingNarrationRef.current = usingNarration;
  }, [usingNarration]);
  useEffect(() => {
    usingExternalRef.current = usingExternal;
  }, [usingExternal]);
  useEffect(() => {
    externalCurrentRef.current = externalCurrent ?? 0;
  }, [externalCurrent]);
  useEffect(() => {
    externalDurationRef.current = externalDuration ?? 0;
  }, [externalDuration]);

  useEffect(() => {
    const loop = () => {
      const audio = audioRef.current;
      const usingNar = usingNarrationRef.current;
      const usingExt = usingExternalRef.current;
      const live = usingExt
        ? externalCurrentRef.current
        : audio && Number.isFinite(audio.currentTime) && (usingNar || !audio.paused)
          ? audio.currentTime
          : elapsedSecondsRef.current;
      const total = usingExt
        ? externalDurationRef.current || totalSecondsRef.current
        : usingNar && audio && Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : totalSecondsRef.current;
      const idx = timedBeatIndex(beats, live, total);
      const prog = timedBeatProgress(beats[idx], live, total);
      const sceneClock = visualClock(playbackFrames, live, total);
      const frameIdx = sceneClock.index;
      const frameProg = sceneClock.progress;
      const dis = hasGallery && frameIdx < gallery.length - 1
        ? Math.max(0, Math.min(1, (frameProg - 0.84) / 0.16)) : 0;
      const idxParity = ((frameIdx % 2) + 2) % 2;
      const camCurrent = cameraSeq[frameIdx % cameraSeq.length];
      const camNext = cameraSeq[(frameIdx + 1) % cameraSeq.length];
      const a = imgARef.current;
      const b = imgBRef.current;
      if (visualBeatRef.current !== idx) {
        visualBeatRef.current = idx;
        setVisualBeatIndex(idx);
      }
      if (visualFrameRef.current !== frameIdx) {
        visualFrameRef.current = frameIdx;
        setVisualFrameIndex(frameIdx);
      }
      const aIsCurrent = idxParity === 0;
      const camA = aIsCurrent ? camCurrent : camNext;
      const camB = aIsCurrent ? camNext : camCurrent;
      const opA = aIsCurrent ? 1 - dis : dis;
      const opB = aIsCurrent ? dis : 1 - dis;
      const fil = filterRef.current;
      if (a) {
        a.style.transform = `translateZ(0) ${cameraTransform(camA, frameProg)}`;
        a.style.opacity = String(opA);
        a.style.filter = `${fil} ${aIsCurrent ? rackFocusBlur(camA, frameProg) : ""}`;
      }
      if (b) {
        b.style.transform = `translateZ(0) ${cameraTransform(camB, frameProg)}`;
        b.style.opacity = String(opB);
        b.style.filter = `${fil} ${!aIsCurrent ? rackFocusBlur(camB, frameProg) : ""}`;
      }
      // Progress bar driven from the same live clock - no setState on scrub,
      // so dragging never triggers React re-render storms that used to
      // freeze the timeline until a page refresh.
      const pct = total > 0 ? Math.max(0, Math.min(100, (live / total) * 100)) : 0;
      const fill = progressFillRef.current;
      const thumb = progressThumbRef.current;
      if (fill) fill.style.width = `${pct}%`;
      if (thumb) thumb.style.left = `${pct}%`;
      const cap = captionRef.current;
      if (cap && cap.dataset.beat !== String(idx)) {
        // The caption HTML itself is owned by React (dangerouslySetInnerHTML
        // reading `beat` state). We only trigger the animation restart here.
        cap.dataset.beat = String(idx);
        cap.style.animation = "none";
        void cap.offsetWidth;
        cap.style.animation = "cinematic-caption 3600ms ease-out both";
      }
      cameraRafRef.current = requestAnimationFrame(loop);
    };
    cameraRafRef.current = requestAnimationFrame(loop);
    return () => {
      if (cameraRafRef.current) cancelAnimationFrame(cameraRafRef.current);
    };
  }, [beats, cameraSeq, gallery, hasGallery]);

  const busy = preparing;

  // ---- Gesture layer: single tap = play/pause, double tap L/R = ±10s ----
  const [flashSkip, setFlashSkip] = useState<null | "back" | "fwd">(null);
  const tapTimerRef = useRef<number | null>(null);
  const lastTapRef = useRef<{ t: number; side: "l" | "r" } | null>(null);
  const flashTimerRef = useRef<number | null>(null);

  function triggerFlash(dir: "back" | "fwd") {
    setFlashSkip(dir);
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(() => setFlashSkip(null), 650);
  }

  function onSurfaceTap(e: React.MouseEvent<HTMLButtonElement>) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const side: "l" | "r" = e.clientX - rect.left < rect.width / 2 ? "l" : "r";
    const now = performance.now();
    const last = lastTapRef.current;
    if (last && now - last.t < 320 && last.side === side) {
      if (tapTimerRef.current) {
        window.clearTimeout(tapTimerRef.current);
        tapTimerRef.current = null;
      }
      lastTapRef.current = null;
      if (side === "l") {
        skip(-10);
        triggerFlash("back");
      } else {
        skip(10);
        triggerFlash("fwd");
      }
      return;
    }
    lastTapRef.current = { t: now, side };
    if (tapTimerRef.current) window.clearTimeout(tapTimerRef.current);
    tapTimerRef.current = window.setTimeout(() => {
      tapTimerRef.current = null;
      lastTapRef.current = null;
      void handleToggle();
    }, 260);
  }

  useEffect(
    () => () => {
      if (tapTimerRef.current) window.clearTimeout(tapTimerRef.current);
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
      if (missionOutroTimerRef.current) window.clearTimeout(missionOutroTimerRef.current);
    },
    [],
  );

  // ---- Fullscreen (immersive, subtitles-only) ----
  // iOS Safari does NOT support Element.requestFullscreen for non-video
  // elements. When native fullscreen is unavailable we fall back to a
  // CSS-based pseudo-fullscreen so the feature is available on every device.
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pseudoFs, setPseudoFs] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  useEffect(() => {
    setIsIOSDevice(isIOSLikeDevice());
  }, []);
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === sectionRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  useEffect(() => {
    // Body scroll lock while pseudo-fullscreen is active.
    if (!pseudoFs) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [pseudoFs]);
  async function toggleFullscreen() {
    const el = sectionRef.current;
    if (isIOSDevice) {
      setPseudoFs((v) => !v);
      return;
    }
    const supportsNative =
      typeof document.fullscreenEnabled === "boolean" &&
      document.fullscreenEnabled &&
      typeof el?.requestFullscreen === "function";
    if (supportsNative) {
      try {
        if (document.fullscreenElement) await document.exitFullscreen();
        else await el!.requestFullscreen();
        return;
      } catch {
        /* fall through to pseudo */
      }
    }
    setPseudoFs((v) => !v);
  }
  const inFullscreen = isFullscreen || pseudoFs;

  // Paused overlay visibility - mirrors the JSX condition below so we can
  // suppress the caption while the CTA is showing (prevents overlap).
  const showPausedOverlay =
    !inFullscreen && ((!narrationStarted && ambient > 2.5) || (!playing && narrationStarted));
  // Show subtitles both in normal AND fullscreen modes once a real transcript
  // exists. Hidden only while the Resume-CTA is visible (no overlap) or during
  // the final mission-outro card.
  // Captions toggle - persisted in localStorage so the user's choice sticks.
  const [ccOn, setCcOn] = useState(true);
  useEffect(() => {
    try {
      const v = window.localStorage.getItem("sa.cc");
      if (v === "0") setCcOn(false);
    } catch {
      /* ignore */
    }
  }, []);
  function toggleCc() {
    setCcOn((v) => {
      const next = !v;
      try {
        window.localStorage.setItem("sa.cc", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }
  const showTimedCaption = hasTimedTranscript && ccOn && !showPausedOverlay && !showMissionOutro;
  const motifEmoji = pickCaptionEmoji(motif, beatIndex, beats.length);

  return (
    <section
      ref={sectionRef}
      aria-label={`${title} - story mode`}
      className={`relative isolate mx-auto w-full overflow-hidden bg-black shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)] ${
        pseudoFs
          ? "fixed inset-0 z-[9999] h-[100dvh] w-[100vw] max-h-none rounded-none"
          : isFullscreen
            ? "h-screen w-screen max-h-none rounded-none"
            : "aspect-[9/16] max-h-[74svh] rounded-2xl md:aspect-video md:max-h-[min(58vh,560px)] md:max-w-4xl"
      }`}
      style={
        pseudoFs
          ? {
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              width: "100vw",
              height: "100dvh",
              maxHeight: "none",
              borderRadius: 0,
            }
          : undefined
      }
    >
      {currentUrl && (
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `url(${currentUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(38px) saturate(1.1) brightness(0.55)",
            transform: "scale(1.25)",
          }}
        />
      )}

      {slotAUrl &&
        (isClipUrl(slotAUrl) ? (
          <video
            key={slotAUrl}
            ref={imgARef as any}
            src={slotAUrl}
            muted
            loop
            autoPlay
            playsInline
            preload="auto"
            aria-hidden
            onError={() => markClipFailed(slotAUrl)}
            className="absolute inset-0 h-full w-full object-cover"
            style={{
              transform: "translateZ(0) scale(1.08)",
              filter,
              opacity: parity === 0 ? 1 : 0,
              backfaceVisibility: "hidden",
              willChange: "transform, filter, opacity",
            }}
          />
        ) : (
          <img
            ref={imgARef as any}
            src={slotAUrl}
            alt=""
            aria-hidden
            decoding="async"
            loading="eager"
            className="absolute inset-0 h-full w-full object-cover"
            style={{
              transform: "translateZ(0) scale(1.08)",
              filter,
              opacity: parity === 0 ? 1 : 0,
              imageRendering: "high-quality" as any,
              backfaceVisibility: "hidden",
              willChange: "transform, filter, opacity",
            }}
          />
        ))}

      {slotBUrl &&
        (isClipUrl(slotBUrl) ? (
          <video
            key={slotBUrl}
            ref={imgBRef as any}
            src={slotBUrl}
            muted
            loop
            autoPlay
            playsInline
            preload="auto"
            aria-hidden
            onError={() => markClipFailed(slotBUrl)}
            className="absolute inset-0 h-full w-full object-cover"
            style={{
              transform: "translateZ(0) scale(1.08)",
              filter,
              opacity: parity === 1 ? 1 : 0,
              backfaceVisibility: "hidden",
              willChange: "transform, filter, opacity",
            }}
          />
        ) : (
          <img
            ref={imgBRef as any}
            src={slotBUrl}
            alt=""
            aria-hidden
            decoding="async"
            loading="eager"
            className="absolute inset-0 h-full w-full object-cover"
            style={{
              transform: "translateZ(0) scale(1.08)",
              filter,
              opacity: parity === 1 ? 1 : 0,
              imageRendering: "high-quality" as any,
              backfaceVisibility: "hidden",
              willChange: "transform, filter, opacity",
            }}
          />
        ))}

      <canvas
        ref={canvasRef}
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full"
        style={{
          mixBlendMode:
            motif === "stars" || motif === "snow" || motif === "embers" ? "screen" : "normal",
        }}
      />

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 30% 15%, rgba(255,240,210,0.18) 0%, transparent 55%), radial-gradient(120% 100% at 70% 100%, rgba(0,0,0,0.65) 20%, transparent 70%)",
        }}
      />

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.09] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          backgroundSize: "260px 260px",
        }}
      />

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ boxShadow: "inset 0 0 180px 30px rgba(0,0,0,0.9)" }}
      />

      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-black/85" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black via-black/70 to-transparent"
      />

      {/* Caption - hidden while paused overlay is showing so it never sits under the CTA.
          Content driven by the rAF loop (see cameraRaf effect) so it stays locked to
          live audio.currentTime instead of drifting behind throttled timeupdate events.
          Emphasis + motif emoji injected via innerHTML for TikTok-style pop. */}
      {showTimedCaption && (
        <div
          className={`pointer-events-none absolute inset-x-0 z-[8] flex justify-center px-5 ${inFullscreen ? "bottom-24 md:bottom-28" : "bottom-16 md:bottom-20"}`}
        >
          <p
            ref={captionRef}
            data-beat="-1"
            data-no-translate
            suppressHydrationWarning
            className={`max-w-[22ch] text-center font-sans font-bold leading-tight tracking-tight text-white drop-shadow-[0_2px_18px_rgba(0,0,0,0.95)] ${inFullscreen ? "text-3xl md:text-4xl" : "text-lg md:text-2xl"}`}
            dangerouslySetInnerHTML={{ __html: renderCaptionHtml(beat?.caption ?? "", motifEmoji) }}
          />
        </div>
      )}

      {showMissionOutro &&
        (() => {
          // Rotate the closing card so viewers don't see the exact same line
          // on every article. The slogan appears ~1 in 3 times as a signature
          // sign-off; the rest stay warm/mission-forward.
          const OUTROS = [
            { line: "No one is stupid - some geniuses are just quietly curious.", slogan: false },
            { line: "Curiosity looks good on you.", slogan: true },
            { line: "Stay curious. It's the smartest thing you can wear.", slogan: false },
            { line: "You just got a little smarter. See you at the next one.", slogan: false },
            { line: "Curiosity looks good on you. Wear it often.", slogan: true },
            {
              line: "The world is weirder than you think - and that's the fun part.",
              slogan: false,
            },
          ];
          let h = 0;
          for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
          const pick = OUTROS[h % OUTROS.length];
          return (
            <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black/45 px-8 backdrop-blur-[2px]">
              <div
                className="max-w-md text-center"
                style={{ animation: "cinematic-invite 520ms ease-out both" }}
              >
                <p className="text-sm font-bold uppercase tracking-[0.28em] text-primary">
                  🦖 Smartasaurus 🧠
                </p>
                <p className="mt-3 text-2xl font-semibold leading-tight text-white drop-shadow-[0_2px_16px_rgba(0,0,0,0.9)] md:text-4xl">
                  {pick.line}
                </p>
                {pick.slogan && (
                  <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.32em] text-white/70">
                    - {ui("The Smartasaurus mindset")} -
                  </p>
                )}
              </div>
            </div>
          );
        })()}

      {/* Top-right controls - CC toggle + Fullscreen */}
      <div className="absolute right-3 top-3 z-40 flex items-center gap-2">
        {hasLocalizedCaptionTrack && (
          <select
            data-no-translate
            value={captionLanguage}
            onChange={(event) => {
              captionChoiceRef.current = { key: `${lang}|${slug}`, picked: true };
              setCaptionLanguage(event.target.value === "localized" ? "localized" : "en");
            }}
            onClick={(event) => event.stopPropagation()}
            aria-label={ui("Caption language")}
            title={ui("Caption language")}
            className="h-9 rounded-full border border-white/15 bg-black/60 px-2 text-[11px] font-bold uppercase text-white backdrop-blur-md"
          >
            <option data-no-translate value="localized">{lang.toUpperCase()}</option>
            <option data-no-translate value="en">EN</option>
          </select>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleCc();
          }}
          aria-label={ui(ccOn ? "Turn captions off" : "Turn captions on")}
          aria-pressed={ccOn}
          title={ui(ccOn ? "Captions on" : "Captions off")}
          className={`grid h-9 min-w-[36px] place-items-center rounded-full border px-2 text-[11px] font-bold uppercase tracking-[0.14em] backdrop-blur-md transition ${
            ccOn
              ? "border-white/25 bg-white/95 text-black hover:bg-white"
              : "border-white/15 bg-black/45 text-white/70 hover:bg-black/70"
          }`}
        >
          CC
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            void toggleFullscreen();
          }}
          aria-label={ui(inFullscreen ? "Exit fullscreen" : "Enter fullscreen")}
          title={ui(inFullscreen ? "Exit fullscreen" : "Fullscreen")}
          className="grid h-9 w-9 place-items-center rounded-full border border-white/15 bg-black/45 text-white/90 backdrop-blur-md transition hover:bg-black/70"
        >
          {inFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
      </div>

      {/* Gesture layer - single tap: play/pause, double tap L/R: ±10s */}
      {narrationStarted && (
        <button
          type="button"
          onClick={onSurfaceTap}
          aria-label={
            ui(playing ? "Tap to pause. Double tap left or right to skip." : "Tap to resume.")
          }
          className="absolute inset-0 z-[5] h-full w-full cursor-pointer bg-transparent"
        />
      )}

      {/* Double-tap skip flash indicator */}
      {flashSkip && (
        <div
          aria-hidden
          className={`pointer-events-none absolute top-1/2 z-[15] -translate-y-1/2 ${flashSkip === "back" ? "left-8" : "right-8"}`}
        >
          <div
            className="flex h-16 w-16 items-center justify-center rounded-full bg-black/55 text-white ring-1 ring-white/20 backdrop-blur-md"
            style={{ animation: "cinematic-flash 650ms ease-out both" }}
          >
            {flashSkip === "back" ? (
              <Rewind className="h-6 w-6" />
            ) : (
              <FastForward className="h-6 w-6" />
            )}
          </div>
          <div
            className="mt-1 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-white/90"
            style={{ animation: "cinematic-flash 650ms ease-out both" }}
          >
            10s
          </div>
        </div>
      )}

      {/* Paused overlay - Resume Story with flanking rwd/fwd icons. Hidden in fullscreen (immersive). */}
      {showPausedOverlay ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center gap-4">
          {narrationStarted && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                skip(-10);
                triggerFlash("back");
              }}
              aria-label={ui("Skip back 10 seconds")}
              className="pointer-events-auto grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-black/55 text-white/95 shadow-[0_8px_24px_rgba(0,0,0,0.45)] backdrop-blur-md ring-1 ring-white/10 transition hover:bg-black/70 active:scale-95"
              style={{ animation: "cinematic-invite 500ms ease-out both" }}
            >
              <Rewind className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={handleToggle}
            disabled={busy}
            className="pointer-events-auto group flex items-center gap-3 rounded-full bg-white/95 px-6 py-3.5 text-black shadow-[0_20px_60px_-10px_rgba(0,0,0,0.9)] backdrop-blur-md transition hover:scale-[1.03] hover:bg-white disabled:opacity-70"
            style={{ animation: "cinematic-invite 700ms ease-out both" }}
            aria-label={ui(busy ? "Preparing story" : narrationStarted ? "Resume story" : "Play story")}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-white">
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4 translate-x-[1px]" />
              )}
            </span>
            <span className="text-sm font-bold uppercase tracking-[0.2em]">
              {ui(busy ? "Preparing…" : narrationStarted ? "Resume Story" : "Play Story")}
            </span>
          </button>
          {narrationStarted && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                skip(10);
                triggerFlash("fwd");
              }}
              aria-label={ui("Skip forward 10 seconds")}
              className="pointer-events-auto grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-black/55 text-white/95 shadow-[0_8px_24px_rgba(0,0,0,0.45)] backdrop-blur-md ring-1 ring-white/10 transition hover:bg-black/70 active:scale-95"
              style={{ animation: "cinematic-invite 500ms ease-out both" }}
            >
              <FastForward className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : null}

      {error && (
        <div className="absolute left-1/2 top-4 z-20 -translate-x-1/2 rounded-full bg-red-500/90 px-4 py-1.5 text-xs font-semibold text-white shadow-lg">
          {error}
        </div>
      )}

      {/* Scrubbable timeline - brand fire color, discreet but always tappable.
          Large invisible hit area (touch-friendly), thin visible bar, tiny thumb
          always shown so the user knows they can drag. */}
      <div
        className="absolute inset-x-0 bottom-0 z-40 touch-none select-none px-3 pb-3 pt-6"
        onPointerDown={(e) => {
          e.stopPropagation();
          const el = audioRef.current;
          if (!el || !Number.isFinite(el.duration) || el.duration <= 0) return;
          const track = e.currentTarget.querySelector("[data-track]") as HTMLElement | null;
          if (!track) return;
          const seekFromEvent = (clientX: number) => {
            const rect = track.getBoundingClientRect();
            const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
            try {
              el.currentTime = pct * el.duration;
            } catch {
              /* ignore */
            }
            // No setState here - rAF loop reads audio.currentTime and paints
            // the bar/thumb via refs each frame. This is what unfroze scrubbing.
          };
          seekFromEvent(e.clientX);
          const target = e.currentTarget;
          try {
            target.setPointerCapture(e.pointerId);
          } catch {}
          const onMove = (ev: PointerEvent) => seekFromEvent(ev.clientX);
          const onUp = (ev: PointerEvent) => {
            target.removeEventListener("pointermove", onMove);
            target.removeEventListener("pointerup", onUp);
            target.removeEventListener("pointercancel", onUp);
            try {
              target.releasePointerCapture(ev.pointerId);
            } catch {}
            // Commit final position to React state once so timeupdate can
            // pick up cleanly from here.
            if (Number.isFinite(el.currentTime)) setCurrent(el.currentTime);
          };
          target.addEventListener("pointermove", onMove);
          target.addEventListener("pointerup", onUp);
          target.addEventListener("pointercancel", onUp);
        }}
        onClick={(e) => e.stopPropagation()}
        role="slider"
        aria-label={ui("Seek timeline")}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progressPct)}
      >
        <div
          data-track
          className="group relative h-[3px] w-full rounded-full bg-white/15 transition-all duration-200 hover:h-[5px]"
        >
          <div
            ref={progressFillRef}
            className="h-full rounded-full"
            style={{
              width: `${Math.min(100, Math.max(0, progressPct))}%`,
              background: "linear-gradient(90deg, var(--fire), oklch(0.78 0.16 55))",
              boxShadow: "0 0 10px color-mix(in oklab, var(--fire) 55%, transparent)",
            }}
          />
          {/* Always-visible discreet thumb - larger touch target on hover/drag */}
          <div
            ref={progressThumbRef}
            className="pointer-events-none absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-90 shadow-[0_2px_6px_rgba(0,0,0,0.55)] transition-all duration-200 group-hover:h-3.5 group-hover:w-3.5 group-hover:opacity-100"
            style={{ left: `${Math.min(100, Math.max(0, progressPct))}%` }}
          />
        </div>
      </div>

      {/* Hidden audio element - the single narration source of truth */}
      {src && (
        <audio
          ref={audioRef}
          src={src}
          preload="auto"
          playsInline
          onCanPlay={(e) => {
            if (!firstAudioLoggedRef.current && startedAtRef.current != null) {
              firstAudioLoggedRef.current = true;
              track("narration_time_to_first_audio_ms", {
                slug,
                ms: Math.round(performance.now() - startedAtRef.current),
              });
            }
            // Intentionally no WebAudio graph: direct element playback is the
            // safest path across iOS, Android and desktop.
            buildAudioGraph();
            const savedProgress = Math.max(pendingResumeRef.current, readStoryProgress(slug));
            if (shouldRestoreStoryAudio(restoredSlugRef.current, slug, pendingResumeRef.current) && Number.isFinite(e.currentTarget.duration)) {
              if (savedProgress > 0) {
              e.currentTarget.currentTime = Math.min(
                savedProgress,
                Math.max(0, e.currentTarget.duration - 1),
              );
              setCurrent(e.currentTarget.currentTime);
              }
              pendingResumeRef.current = 0;
              restoredSlugRef.current = slug;
            }
            if (audioCtxRef.current?.state === "suspended") {
              audioCtxRef.current.resume().catch(() => {});
            }
            if (autoPlayRef.current) {
              autoPlayRef.current = false;
              e.currentTarget.play().catch(() => setError(ui("Playback failed. Tap Resume Story again.")));
            }
          }}
          onPlay={(e) => {
            setPlaying(true);
            setShowMissionOutro(false);
            if (missionOutroTimerRef.current) {
              window.clearTimeout(missionOutroTimerRef.current);
              missionOutroTimerRef.current = null;
            }
            const el = e.currentTarget;
            // Natural, non-shouty target volume; smooth fade-in.
            el.volume = 0;
            const target = 0.72;
            const start = performance.now();
            const tick = (now: number) => {
              const t = Math.min(1, Math.max(0, (now - start) / 350));
              el.volume = Math.max(0, Math.min(1, target * t));
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            startSilenceMonitor();
            try {
              if ("mediaSession" in navigator) {
                navigator.mediaSession.metadata = new MediaMetadata({
                  title,
                  artist: "Smartasaurus",
                  album: categoryLabel ?? "Discovery",
                });
                navigator.mediaSession.setActionHandler("play", () => {
                  void handleToggle();
                });
                navigator.mediaSession.setActionHandler("pause", () => {
                  void handleToggle();
                });
                navigator.mediaSession.setActionHandler("seekbackward", () => skip(-15));
                navigator.mediaSession.setActionHandler("seekforward", () => skip(15));
                navigator.mediaSession.playbackState = "playing";
              }
            } catch {
              /* ignore */
            }
            track("narration_started", { slug });
            trackConversionOnce(`audio_start.${slug}`, "audio_start", `/discovery/${slug}`);
            window.dispatchEvent(
              new CustomEvent("smartasaurus:narration", {
                detail: { playing: true, slug, lang: "en", source: "english" },
              }),
            );
          }}
          onPause={() => {
            const el = audioRef.current;
            if (el) writeStoryProgress(slug, el.currentTime, el.duration);
            setPlaying(false);
            stopSilenceMonitor();
            try {
              if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused";
            } catch {
              /* ignore */
            }
            window.dispatchEvent(
              new CustomEvent("smartasaurus:narration", {
                detail: { playing: false, slug, lang: "en", source: "english" },
              }),
            );
            window.dispatchEvent(
              new CustomEvent("smartasaurus:narration-silence", { detail: { silent: true } }),
            );
          }}
          onEnded={() => {
            clearStoryProgress(slug);
            pendingResumeRef.current = 0;
            setPlaying(false);
            setCurrent(0);
            setShowMissionOutro(true);
            if (missionOutroTimerRef.current) window.clearTimeout(missionOutroTimerRef.current);
            missionOutroTimerRef.current = window.setTimeout(() => {
              setShowMissionOutro(false);
              missionOutroTimerRef.current = null;
            }, 7200);
            stopSilenceMonitor();
            try {
              if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "none";
            } catch {
              /* ignore */
            }
            track("narration_completed", { slug });
            trackConversion("audio_complete", `/discovery/${slug}`);
            window.dispatchEvent(
              new CustomEvent("smartasaurus:narration", {
                detail: { playing: false, slug, lang: "en", source: "english" },
              }),
            );
            window.dispatchEvent(
              new CustomEvent("smartasaurus:narration-silence", { detail: { silent: true } }),
            );
            // The translated track owns completion while it is active. A stale
            // muted English `ended` event must never advance the article.
            if (!silentNarration) {
              window.dispatchEvent(
                new CustomEvent("smartasaurus:narration-ended", {
                  detail: { slug, lang: "en", source: "english" },
                }),
              );
            }
          }}
          onError={(e) => {
            const el = e.currentTarget;
            writeStoryProgress(slug, el.currentTime || current, el.duration);
            if (!recoveryAttemptedRef.current && src) {
              recoveryAttemptedRef.current = true;
              pendingResumeRef.current = Math.max(pendingResumeRef.current, el.currentTime, current);
              el.src = src;
              el.load();
              return;
            }
            setError(ui("Playback failed. Tap Resume Story again."));
          }}
          onLoadedMetadata={(e) => {
            setDuration(e.currentTarget.duration);
            const savedProgress = Math.max(pendingResumeRef.current, readStoryProgress(slug));
            if (shouldRestoreStoryAudio(restoredSlugRef.current, slug, pendingResumeRef.current)) {
              if (savedProgress > 0) {
              e.currentTarget.currentTime = Math.min(
                savedProgress,
                Math.max(0, e.currentTarget.duration - 1),
              );
              setCurrent(e.currentTarget.currentTime);
              }
              pendingResumeRef.current = 0;
              restoredSlugRef.current = slug;
            }
            window.dispatchEvent(
              new CustomEvent("smartasaurus:narration-progress", {
                detail: {
                  currentTime: e.currentTarget.currentTime,
                  duration: e.currentTarget.duration,
                },
              }),
            );
          }}
          onTimeUpdate={(e) => {
            setCurrent(e.currentTarget.currentTime);
            recoveryAttemptedRef.current = false;
            const second = Math.floor(e.currentTarget.currentTime);
            if (second >= lastSavedSecondRef.current + 2) {
              lastSavedSecondRef.current = second;
              writeStoryProgress(slug, e.currentTarget.currentTime, e.currentTarget.duration);
            }
            window.dispatchEvent(
              new CustomEvent("smartasaurus:narration-progress", {
                detail: {
                  currentTime: e.currentTarget.currentTime,
                  duration: e.currentTarget.duration,
                },
              }),
            );
          }}
          className="hidden"
        />
      )}

      <style>{`
        /* Caption entrance only - stays fully visible until the beat changes,
           which restarts the animation from the top. No fade-to-zero (that
           bug is what made captions disappear after 3.6s and only reappear
           at the outro). */
        @keyframes cinematic-caption {
          0%   { opacity: 0; transform: translateY(10px) scale(0.98); filter: blur(6px); }
          22%  { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes cinematic-invite {
          0%   { opacity: 0; transform: translateY(14px) scale(0.94); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes cinematic-flash {
          0%   { opacity: 0; transform: scale(0.7); }
          25%  { opacity: 1; transform: scale(1.05); }
          75%  { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.1); }
        }
        /* Static gradient underline - no pop/flash animation so the eye
           can rest on the emphasized word. */
        .cap-emph {
          background: linear-gradient(180deg, transparent 62%, color-mix(in oklab, var(--fire) 55%, transparent) 62%);
          color: #fff;
          font-weight: 800;
          padding: 0 2px;
          border-radius: 3px;
        }
        .cap-emoji {
          display: inline-block;
          margin-left: 6px;
          transform: translateY(1px);
          filter: drop-shadow(0 2px 6px rgba(0,0,0,0.6));
        }
      `}</style>
    </section>
  );
}
